import React, { useEffect, useRef, useState } from 'react';
import { Input, ListGroup, ListGroupItem } from 'reactstrap';
import { getPlacesEndpoint } from '../../../services/addressService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';

export interface AdvisoryZoneSuggestion {
  id: string;
  value: string;
  level: number;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  // Se dispara con la sugerencia elegida (id/level), o `null` cuando el
  // texto deja de corresponder a una sugerencia real (tipeo libre o clear)
  // — mismo contrato que ZoneSearchInput del sitio público (propi-web-nextjs).
  onSelectZone: (item: AdvisoryZoneSuggestion | null) => void;
  id?: string;
}

// Mismo endpoint público `/search` que usa el buscador de zona del form de
// Asesoría en propi-web-nextjs (src/components/page/asesoria/components/ZoneSearchInput.tsx)
// — acá pegado vía `getPlacesEndpoint` (ya existente en este repo, usado por
// PlaceSearchBar para lugares cercanos de inversores). Se filtra a
// level 3/4 (zona/colonia) igual que el sitio público.
const AdvisoryZoneSearchInput: React.FC<Props> = ({ value, onChange, onSelectZone, id }) => {
  const { callEndpoint } = useCallApiAndLoad();
  const [suggestions, setSuggestions] = useState<AdvisoryZoneSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const search = async (query: string) => {
    const requestId = ++requestIdRef.current;
    const { data } = await callEndpoint(
      getPlacesEndpoint({ input: query, not_found: '', sid: '' }),
    );
    let hits: AdvisoryZoneSuggestion[] = Array.isArray(
      fetchResponseAdapter(data)?.data,
    )
      ? fetchResponseAdapter(data).data
      : [];

    if (hits.length === 0) {
      const fallback = await callEndpoint(
        getPlacesEndpoint({ input: query, not_found: '1', sid: '' }),
      );
      hits = Array.isArray(fetchResponseAdapter(fallback.data)?.data)
        ? fetchResponseAdapter(fallback.data).data
        : [];
    }

    if (requestId === requestIdRef.current) {
      setSuggestions(hits.filter((item) => item.level === 3 || item.level === 4));
    }
  };

  const handleInputChange = (val: string) => {
    onChange(val);
    onSelectZone(null);
    setIsOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim() === '') {
        requestIdRef.current++;
        setSuggestions([]);
      } else {
        search(val).catch(console.error);
      }
    }, 150);
  };

  const handleSelect = (item: AdvisoryZoneSuggestion) => {
    onChange(item.value);
    onSelectZone(item);
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    onSelectZone(null);
    setSuggestions([]);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Input
        id={id}
        type="text"
        placeholder="Buscar zona..."
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => value.trim() && setIsOpen(true)}
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Limpiar búsqueda"
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            border: 'none',
            background: 'transparent',
            color: '#9E9E9E',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      )}
      {isOpen && suggestions.length > 0 && (
        <ListGroup
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {suggestions.map((item) => (
            <ListGroupItem
              key={item.id}
              action
              onClick={() => handleSelect(item)}
              style={{ cursor: 'pointer' }}
            >
              {item.value}
            </ListGroupItem>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default AdvisoryZoneSearchInput;
